import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Check, Phone, Star, HelpCircle, Cloud } from "lucide-react";
import { publicApi } from "../lib/api";
import { getLocation } from "../data/locations";
import { getServiceLocation, SERVICES, SERVICE_LOCATIONS } from "../data/serviceLocations";
import { xeroSlugForTown } from "../data/xeroLocations";
import { getCountyCopy } from "../data/countyCopy";
import Seo, { breadcrumbSchema, faqSchema, logoUrl } from "../components/Seo";
import NotFound from "./NotFound";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import BookCallButton from "../components/BookCallButton";

const siteOrigin = () => (typeof window !== "undefined" ? window.location.origin : "");

export default function ServiceLocationPage({ slug }) {
  const entry = getServiceLocation(slug);
  const svc = entry ? SERVICES[entry.service] : null;
  const loc = entry ? getLocation(entry.baseSlug) : null;
  const [rating, setRating] = useState(null);
  const [testimonial, setTestimonial] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    publicApi.googleRating().then(setRating).catch(() => {});
    if (entry) {
      publicApi.townTestimonials()
        .then((all) => setTestimonial((all || {})[entry.baseSlug] || null))
        .catch(() => setTestimonial(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!entry || !svc || !loc) return <NotFound />;

  const county = getCountyCopy(loc.county);
  const faqs = [...(entry.faqs || []), ...svc.sharedFaqs];
  const xeroSlug = xeroSlugForTown(loc.slug);
  // Other combo pages for this same town (cross-linking), excluding the current one.
  const townCombos = SERVICE_LOCATIONS.filter((l) => l.baseSlug === entry.baseSlug && l.slug !== slug);
  const otherTowns = SERVICE_LOCATIONS.filter((l) => l.service === entry.service && l.baseSlug !== entry.baseSlug).slice(0, 6);

  const schema = {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    name: `${svc.label} in ${loc.town} — Cloud Accounting Support Services`,
    description: entry.metaDescription,
    url: `${siteOrigin()}/${entry.slug}`,
    logo: logoUrl(),
    telephone: "+44 1827 780550",
    email: "info@cass-online.co.uk",
    areaServed: [loc.town, ...loc.nearby],
    knowsAbout: svc.knowsAbout,
    address: {
      "@type": "PostalAddress",
      streetAddress: "3 Pebble Close, Amington",
      addressLocality: "Tamworth",
      addressRegion: "Staffordshire",
      postalCode: "B77 4RD",
      addressCountry: "GB",
    },
    // Reviews of CASS itself are intentionally NOT marked up here (visible Google
    // reviews remain on the page). This avoids self-serving review-snippet markup.
  };

  return (
    <div className="bg-white" data-testid={`service-location-page-${entry.slug}`}>
      <Seo title={`${svc.label} in ${loc.town}`} description={entry.metaDescription}
        jsonLd={[
          schema,
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: `${svc.label} in ${loc.town}`, path: `/${entry.slug}` }]),
          faqSchema(faqs),
        ]} />

      {/* Hero */}
      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase mb-4">
            <MapPin size={16} /> {svc.eyebrow} · {loc.town}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">{svc.label} in {loc.town}</h1>
          {rating?.rating && (
            <a href={rating.url || rating.write_review_url || "#"} target="_blank" rel="noopener noreferrer"
              data-testid="service-rating-snippet"
              className="inline-flex items-center gap-2 mt-5 bg-white/5 border border-white/10 rounded-full pl-2.5 pr-4 py-1.5 hover:bg-white/10 transition-colors">
              <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-white/20 text-white/20"} />)}</span>
              <span className="text-sm font-semibold text-white">{rating.rating}/5</span>
              <span className="text-sm text-slate-300">· {rating.total} Google reviews</span>
            </a>
          )}
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">{entry.intro[0]}</p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/contact" data-testid="service-cta-talk"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="service-book-call" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
          </div>
        </div>
      </section>

      {/* Intro continued */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{entry.intro[1]}</p>
        {(entry.intro.slice(2) || []).map((para, i) => (
          <p key={i} className="text-slate-600 text-lg leading-relaxed max-w-3xl mt-5">{para}</p>
        ))}
      </section>

      {/* Service features */}
      <section className="bg-slate-50 py-16 md:py-20" data-testid="service-features">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-orange">What's included</span>
          <h2 className="text-3xl font-extrabold text-navy mt-1">{svc.label} for {loc.town} businesses</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {svc.features.map(([Icon, title, desc]) => (
              <div key={title} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center mb-4"><Icon size={20} /></div>
                <h3 className="font-display font-bold text-navy">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-2">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why local (per county) */}
      {county && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="service-county-copy">
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy">{county.heading}</h2>
          <p className="text-slate-600 leading-relaxed mt-4 max-w-3xl">{county.body}</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {county.points.map((p) => (
              <div key={p} className="flex items-start gap-2.5 bg-slate-50 rounded-xl border border-slate-100 p-4"><Check size={18} className="text-orange shrink-0 mt-0.5" /><span className="text-sm font-medium text-navy leading-snug">{p}</span></div>
            ))}
          </div>
        </section>
      )}

      <GoogleReviewsSection testId="service-combo-reviews" eyebrow="Trusted locally"
        offset={Math.max(0, SERVICE_LOCATIONS.findIndex((l) => l.slug === slug))} />

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="service-faqs">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange shrink-0"><HelpCircle size={22} /></span>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-orange">{svc.label} in {loc.town}</span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-navy leading-tight">Frequently asked questions</h2>
          </div>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`svc-faq-${i}`} data-testid={`service-faq-${i}`} className="bg-slate-50 rounded-xl border border-slate-100 px-5">
              <AccordionTrigger className="text-left font-display font-bold text-navy hover:no-underline">{f.question}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Cross-links: general town page, xero, other combos for this town */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-4" data-testid="service-crosslinks">
        <div className="flex flex-wrap gap-3">
          <Link to={`/${loc.slug}`} data-testid="service-to-general" className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:-translate-y-0.5 transition-all"><MapPin size={14} className="text-orange" /> Accountants in {loc.town}</Link>
          {xeroSlug && (
            <Link to={`/${xeroSlug}`} data-testid="service-to-xero" className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:-translate-y-0.5 transition-all"><Cloud size={14} className="text-orange" /> Xero accountants in {loc.town}</Link>
          )}
          {townCombos.map((c) => (
            <Link key={c.slug} to={`/${c.slug}`} data-testid={`service-to-${c.slug}`} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:-translate-y-0.5 transition-all">{SERVICES[c.service].label} in {loc.town}</Link>
          ))}
        </div>
      </section>

      {/* Same service in nearby towns */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="service-related">
        <h2 className="text-2xl font-extrabold text-navy">{svc.label} in nearby towns</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {otherTowns.map((c) => {
            const t = getLocation(c.baseSlug);
            return (
              <Link key={c.slug} to={`/${c.slug}`} data-testid={`service-related-${c.slug}`}
                className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-orange hover:-translate-y-0.5 transition-all group">
                <span className="inline-flex items-center gap-2 font-semibold text-navy text-sm"><MapPin size={15} className="text-orange" /> {svc.label} in {t?.town}</span>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-orange" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Let's sort your {svc.label.toLowerCase()} in {loc.town}</h2>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Book a free, no-obligation chat. We'll learn about your business and show you exactly how we can help.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="service-book-call-cta" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
            <a href="tel:+441827780550"><Button variant="outline" className="rounded-full px-8 py-6 text-base font-bold border-white/25 text-white hover:bg-white/10 bg-transparent"><Phone size={16} className="mr-2" /> 01827 780550</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
